import {useCallback, useMemo} from 'react';
import {ProfileEnum} from 'presentation/constants';
import {useReverseGeolocation} from 'presentation/hooks/useReverseGeolocation';
import {useLocation, useNavigate} from 'react-router-dom';
import {FavoriteAddressesItem} from '../../../../types';
import {formatReverseGeocodeAddressLabel, isAddressAlreadySaved} from '../../../../utils/address';
import {CurrentLocationState, UseCurrentLocationReturn} from './types';

export function useCurrentLocation(
	favoriteAddresses: FavoriteAddressesItem[],
): UseCurrentLocationReturn {
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {permission, reverseGeocodeData, reverseGeocodeError, geolocationError, isLoading} =
		useReverseGeolocation({autoRequestOnMount: true});

	const alreadySaved = useMemo(() => {
		if (!reverseGeocodeData) return false;
		return isAddressAlreadySaved({
			street: reverseGeocodeData.street,
			city: reverseGeocodeData.city,
			state: reverseGeocodeData.state,
			cep: reverseGeocodeData.cep,
			list: favoriteAddresses,
		});
	}, [reverseGeocodeData, favoriteAddresses]);

	const addressLabel = useMemo(() => {
		return formatReverseGeocodeAddressLabel({reverseGeocodeData});
	}, [reverseGeocodeData]);

	const hasPermission = permission === 'granted' || permission === 'prompt';
	const hasValidAddress = !!reverseGeocodeData?.city && !!reverseGeocodeData?.state;
	const hasError = !!geolocationError || !!reverseGeocodeError;

	const state = useMemo(() => {
		if (!hasPermission) return CurrentLocationState.HIDDEN;
		if (isLoading) return CurrentLocationState.LOADING;
		if (hasError || !hasValidAddress || alreadySaved) return CurrentLocationState.HIDDEN;
		return CurrentLocationState.READY;
	}, [hasPermission, isLoading, hasError, hasValidAddress, alreadySaved]);

	const handleAdd = useCallback(() => {
		if (!reverseGeocodeData) return;

		navigate(ProfileEnum.FAVORITE_ADDRESSES_ADD, {
			state: {
				from: pathname,
				initialReverseGeocodeData: reverseGeocodeData,
			},
		});
	}, [navigate, pathname, reverseGeocodeData]);

	return {
		state,
		addressLabel,
		isAdding: false,
		handleAdd,
	};
}
